import BaseItemView from 'app/src/views/base/BaseItemView';
import Customer from 'app/src/data/models/Customer';
import Format from 'app/src/data/Format';
import Decimal from 'app/src/data/base/Decimal';
import Localize from 'app/src/localize/Localize';
import Filter from 'app/src/data/models/utils/Filter';
import PaymentServiceAlert from 'app/src/controllers/payments/PaymentServiceAlert';
import $ from 'jquery';
import paymentCreditDebitTemplate from './templates/payment-credit-debit-view.hbs';

// events triggered by a PaymentCreditDebitView
const _Event = {
  CREDIT_DEBIT_CLICKED: 'credit:debit:clicked',
  CREDIT_DEBIT_CANCELLED: 'credit:debit:cancelled',
  AMOUNT_CHANGED: 'amount:changed',
  SELECTED_TERMINAL_CHANGED: 'selected:terminal:changed',
  START_PAYMENT_CLICKED: 'start:payment:clicked',
  VOID_PAYMENT_BUTTON_CLICKED: 'void:payment:button:clicked',
  ACCEPT_PAYMENT_BUTTON_CLICKED: 'accept:payment:button:clicked',
  MANUAL_ENTRY_BUTTON_CLICKED: 'manual:entry:button:clicked',
  PROCESS_BUTTON_CLICKED: 'process:button:clicked',
  CREDIT_INPUT_KEYPRESS: 'credit:input:keypress',
  PRINT_ERROR_RECEIPT_BUTTON_CLICKED: 'print:error:receipt:button:clicked',
};

// configure
let View = BaseItemView.extend({
  template: paymentCreditDebitTemplate,

  ui: {
    amount: '#credit_card_amount',
    terminalSelect: '#terminal_select',
    paymentTerminalSelect: '#payment_terminal_select',
    swipePinPadEntry: '#swipecard_pinpad_entry',
    startPaymentButton: '#emv_payment_start',
    paymentStartBlock: '#emv-payment-start',
    paymentOptionsBlock: '#payment-options',
    paymentErrorBlock: '#emv-error',
    paymentRetryButton: '#emv_error_retry_button',
    printErrorReceiptButton: '#emv_print_failed_receipt',
    paymentErrorBlockCancelButton: '#emv_error_cancel_button',
    paymentApprovedWithErrorBlock: '#emv-approve-error',
    paymentErrorVoidButton: '#emv_error_void_button',
    paymentErrorAcceptButton: '#emv_error_accept_button',
    manualEntryButton: '#manual_entry_button',
    processButton: '#processButton',
    cancelButton: '#cancelButton',
    creditCardInput: '#credit_card_input',
  },

  events: {
    'change @ui.amount': 'onAmountChanged',
    'change @ui.terminalSelect': 'onTerminalSelectChanged',
    'click @ui.startPaymentButton': 'onStartPaymentButtonClicked',
    'click @ui.paymentRetryButton': 'onRetryPaymentButtonClicked',
    'click @ui.manualEntryButton': 'onManualEntryButtonClicked',
    'click @ui.processButton': 'onProcessButtonClicked',
    'keyup @ui.creditCardInput': 'onCreditInputKeypress',
  },

  triggers: {
    'click @ui.cancelButton': _Event.CREDIT_DEBIT_CANCELLED,
    'click @ui.paymentErrorBlockCancelButton': _Event.CREDIT_DEBIT_CANCELLED,
    'click @ui.paymentErrorVoidButton': _Event.VOID_PAYMENT_BUTTON_CLICKED,
    'click @ui.paymentErrorAcceptButton': _Event.ACCEPT_PAYMENT_BUTTON_CLICKED,
    'click @ui.printErrorReceiptButton': _Event.PRINT_ERROR_RECEIPT_BUTTON_CLICKED,
  },
});

// Defined
export default class PaymentDefaultView extends View {
  static get Event() {
    return _Event;
  }

  // Handlebars Template to use
  getTemplate() {
    return paymentCreditDebitTemplate;
  }

  /**
   * Event handler for when the amount text field has changed.  Triggers an event with the
   * new value of the amount.
   */
  onAmountChanged() {
    let amount = Format.parseDecimal(this.ui.amount.val());
    this.trigger(_Event.AMOUNT_CHANGED, amount);
    this.setAmount(amount);
  }

  /**
   * When the dropdown for the Terminal Select is changed to USB, call showSwipePinPadEntry().
   * Triggers a terminal selected event with ID of the selected terminal.
   *
   * @param event
   */
  onTerminalSelectChanged(event) {
    const $terminalSelect = $(event.target);
    if ($terminalSelect.val() === 'USB') {
      this.showSwipePinPadEntry();
    }

    let terminalID = $terminalSelect.find(':selected').attr('data-term-id');
    if (terminalID && terminalID.match && !terminalID.match(/-/)) {
      // the ID may be a UUID for certain types of terminals (e.g. Blackline)
      terminalID = Format.parseInt(terminalID);
    }

    this.trigger(_Event.SELECTED_TERMINAL_CHANGED, terminalID);
  }

  /**
   * Event handler for when the start payment button is clicked.  Triggers an event
   * with the current amount.
   */
  onStartPaymentButtonClicked() {
    const amountEntered = Format.parseDecimal(this.ui.amount.val(), new Decimal(0));
    if (amountEntered.isZero()) {
      alertUser(Localize.translate('Please enter a proper charge amount'));
    } else if (this.ui.terminalSelect.find(':selected').data('termId') !== 'select-terminal') {
      this.enableStartPaymentsButton(false);
      this.trigger(_Event.START_PAYMENT_CLICKED, amountEntered, false);
    }
  }

  /**
   * Event handler for when the retry payment button is clicked.  Triggers an event
   * with the current amount.
   */
  onRetryPaymentButtonClicked() {
    this.enableRetryButton(false);
    this.trigger(_Event.START_PAYMENT_CLICKED, Format.parseDecimal(this.ui.amount.val()), true);
  }

  /**
   * Event handler for when the process button is clicked.  Triggers an event with the
   * value of the credit card input field.
   */
  onProcessButtonClicked() {
    this.trigger(_Event.PROCESS_BUTTON_CLICKED, this.ui.creditCardInput.val());
  }

  onCreditInputKeypress(event) {
    this.trigger(_Event.CREDIT_INPUT_KEYPRESS, this.ui.creditCardInput.val(), event);
  }

  /**
   * UI Show/Hide to SHOW the PinPad Entry, and HIDE the PaymentTerminal Dropdown
   */
  showSwipePinPadEntry() {
    this.ui.paymentTerminalSelect.hide();
    this.ui.startPaymentButton.hide();
    this.ui.swipePinPadEntry.show();
    this.ui.processButton.show();
    this.ui.creditCardInput.trigger('focus');
  }

  /**
   * Disable/Enable the Process button for swipe payments
   *
   * @param {Bool} isDisabled When true the process button will be disabled
   */
  setProcessButtonDisabled(isDisabled) {
    this.ui.processButton.prop('disabled', isDisabled);
  }

  /**
   * ***************
   * *** HANDLER ***
   * ***************
   *
   * Entry Point for the "Manual Entry" which trigger the
   * MANUAL_ENTRY_BUTTON_CLICKED event
   *
   * This event triggers the calls "onManualEntryButtonClicked"
   * inside the "PaymentCreditDebitViewController" along with
   * the "onChildViewManualEntryButtonClicked" in the "PaymentViewController"
   * which is for the legacy gateways
   */
  onManualEntryButtonClicked() {
    let filter = new Filter().loadRelations('all');
    let customer = new Customer({ customerID: this.viewData.saleData.sale.customerID });

    if (this.getAmount().isZero()) {
      alertUser(Localize.translate('Please enter a proper charge amount'));
    } else {
      if (this.viewData.hasSwipePayment) {
        PaymentServiceAlert.alert(
          "Can't swipe and manually process credit cards on the same sale. " +
            'To finish this sale, please either swipe the credit card or use a different payment type. ' +
            'Alternatively, remove this credit card payment and process the entire sale by manual entry.'
        );
      } else if (customer.get('customerID') !== 0) {
        $.when(customer.fetch(filter)).done(() => {
          this.trigger(
            _Event.MANUAL_ENTRY_BUTTON_CLICKED,
            customer,
            this.viewData.isDeposit,
            this.getAmount()
          );
        });
      } else {
        this.trigger(
          _Event.MANUAL_ENTRY_BUTTON_CLICKED,
          customer,
          this.viewData.isDeposit,
          this.getAmount()
        );
      }
    }
  }

  /**
   * Set the value of the amount field.
   *
   * @param {Decimal} amount  The new amount
   */
  setAmount(amount) {
    this.ui.amount.val(Format.formatMoney(amount, 0, false));
  }

  /**
   * Get the value of the amount field.
   *
   * @returns {*|Decimal} The amount
   */
  getAmount() {
    return Format.parseDecimal(this.ui.amount.val());
  }

  /**
   * Selects the terminal with the specified ID from the terminal dropdown
   *
   * @param {Number} terminalID   The terminal ID
   */
  setTerminalSelected(terminalID) {
    if (terminalID) {
      this.ui.paymentTerminalSelect
        .find(`option[data-term-id=${terminalID}]`)
        .attr('selected', true);
    }
  }

  /**
   * Set the name of the terminal with the specified ID to the text specified.
   *
   * @param {Number} terminalID   The terminal ID
   * @param {string} terminalName  The text to be set for the terminal name in
   *                              the terminal dropdown.
   */
  setTerminalName(terminalID, terminalName) {
    if (terminalID) {
      this.ui.paymentTerminalSelect.find(`option[data-term-id=${terminalID}]`).text(terminalName);
    }
  }

  /**
   * Enables/disables the start payment button
   *
   * @param {boolean} enabled  When true button is enabled
   */
  enableStartPaymentsButton(enabled) {
    this.ui.startPaymentButton.prop('disabled', !enabled);
  }

  /**
   * Enables/disables the retry payment button
   *
   * @param {boolean} enabled  When true button is enabled
   */
  enableRetryButton(enabled) {
    this.ui.paymentRetryButton.prop('disabled', !enabled);
  }

  /**
   * Shows the payment error section of the view while hiding others
   *
   * @param {string}  reason    General description of the error
   * @param {string}  error     The error
   * @param {boolean} hidePrintReceipt  When true the print receipt button will be hidden
   */
  showPaymentError(reason, error = '', hidePrintReceipt = false) {
    this._hidePaymentBlocks();

    this.ui.paymentErrorBlock.find('h1').text(reason);
    this.ui.paymentErrorBlock.find('p.explanation').text(error || '');

    if (hidePrintReceipt) {
      this.ui.printErrorReceiptButton.hide();
    } else {
      this.ui.printErrorReceiptButton.show();
    }

    this.ui.paymentErrorBlock.show();
  }

  /**
   * Shows the payment approved with error section of the view while hiding others
   *
   * @param {string} error  The error to display
   */
  showApprovedWithError(error) {
    this._hidePaymentBlocks();

    this.ui.paymentApprovedWithErrorBlock.find('p.error-explanation').text(error);

    this.ui.paymentApprovedWithErrorBlock.show();
  }

  /**
   * Hides the Manual Entry Button
   * e.g. For when the Active Gateway is set to Vantiv, call this from the Controller to hide the Manual Entry Button
   * as it is not supported.
   */
  hideManualEntryButton() {
    this.ui.manualEntryButton.hide();
  }

  /**
   * Hides all sections that are used during a terminal transaction.
   *
   * @private
   */
  _hidePaymentBlocks() {
    this.ui.paymentStartBlock.hide();
    this.ui.paymentOptionsBlock.hide();
    this.ui.paymentErrorBlock.hide();
    this.ui.paymentApprovedWithErrorBlock.hide();
  }

  /**
   * Highlights the contents of the Credit Card Input
   */
  selectCreditCardInput() {
    this.ui.creditCardInput.trigger('select');
  }

  disableCreditCardAmountInput() {
    this.ui.amount.prop('disabled', 'disabled');
  }
}
